import React, { useEffect, useState } from "react";
import Avvvatars from "avvvatars-react";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import EDTTooltipOption from "./student/edt/tooltipOption";
import { getEDTByClass } from "../../api/edt";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import { toast } from "sonner";
import {
  getBreaksByYear,
  getSessionsByYear,
  getSettingByYear,
} from "../../api/settingEDT";
import { toMinutes } from "../../helper/toMinutes";
import { getColorPair } from "../../helper/randowColor";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

interface StudentEdtPageProps {
  id_class?: number;
  isOpen: boolean;
}

const StudentEdtPage = ({ id_class, isOpen }: StudentEdtPageProps) => {
  const { selectedYear } = useAcademicYear();

  const [days, setDays] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [allBreaks, setAllBreaks] = useState<any[]>([]);
  const [courses, setCourses] = useState<any>({});
  const [selectedTimetable, setSelectedTimetable] = useState<any>();
  const [selectedTimetableData, setSelectedTimetableData] = useState<any>();
  const [loadingEDT, setLoadingEDT] = useState<boolean>(false);

  //  fonction pour récupérer les cours d'un EDTVersion
  const fetchAllCourseOfEDT = async () => {
    try {
      setLoadingEDT(true);
      if (!selectedYear || !selectedTimetable) {
        return;
      }

      const response = await getEDTByClass(
        id_class,
        selectedTimetable?.VersionEDTs[0]?.id,
        buildPermissionContext(Resource.STUDENT, Action.READ),
      );
      // console.log("Courses Response: ", response);
      setSelectedTimetableData(response.data[0]);
    } catch (error: any) {
      setLoadingEDT(false);
      // console.log("erreur de chargement des coursEDTs : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    fetchAllCourseOfEDT();
  }, [selectedTimetable, selectedYear]);

  // fonction pour la récupération des jours ouvrables
  const fetchWorkingDays = async () => {
    try {
      if (!selectedYear?.id) return;
      const response = await getSettingByYear(
        selectedYear.id,
        buildPermissionContext(Resource.STUDENT, Action.READ),
      );

      // Convertit la chaîne JSON reçue en tableau de jours
      const rawDays: string = response.data[0].working_days; // ex: "{\"Lundi\",\"Dimanche\"}"
      const cleanedDays = rawDays.replace(/[{}]/g, ""); // retire accolades
      const daysArray = cleanedDays
        .split(",")
        .map((d) => d.replace(/"/g, "").trim()); // ["Lundi", "Dimanche"]

      // Ordre des jours de la semaine
      const weekOrder = [
        "Lundi",
        "Mardi",
        "Mercredi",
        "Jeudi",
        "Vendredi",
        "Samedi",
        "Dimanche",
      ];
      const sortedDays = daysArray.sort(
        (a, b) => weekOrder.indexOf(a) - weekOrder.indexOf(b),
      );

      const formattedDays = sortedDays.map((day: any, idx: number) => ({
        id: `d${idx + 1}`,
        name: day,
      }));

      setDays(formattedDays);
    } catch (error: any) {
      // console.log("erreur de chargement des jours ouvrables : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // fonction pour récupérer toutes les séances
  const fetchAllSessions = async () => {
    try {
      if (!selectedYear?.id) return;

      const response = await getSessionsByYear(
        selectedYear?.id,
        buildPermissionContext(Resource.STUDENT, Action.READ),
      );
      const formattedSessions = response.data.map(
        (session: any, index: number) => ({
          label: session.name,
          start_time: session.start_time?.slice(0, 5), // pour enlever les secondes
          end_time: session.end_time?.slice(0, 5),
          time: `${session.start_time?.slice(0, 5)} - ${session.end_time?.slice(
            0,
            5,
          )}`,
          index: index + 1,
          id: session.id,
          duration:
            toMinutes(session.end_time?.slice(0, 5)) -
            toMinutes(session.start_time?.slice(0, 5)),
        }),
      );
      setSessions(formattedSessions);
      // console.log("Seances récupéré: ",formattedSessions)
    } catch (error: any) {
      // console.log("erreur de chargement des séances : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // Fonction pour récupérer les pauses d'une année
  const fetchEDTBreaksByYear = async () => {
    try {
      if (!selectedYear) return;
      const response = await getBreaksByYear(
        selectedYear?.id,
        buildPermissionContext(Resource.STUDENT, Action.READ),
      );
      // console.log("response data break: ",response.data)
      const formattedBreaks = response.data.map(
        (breakItem: any, index: number) => ({
          id: breakItem.id,
          label: breakItem.name,
          start: breakItem.start_time?.slice(0, 5), // pour enlever les secondes
          end: breakItem.end_time?.slice(0, 5),
        }),
      );
      // console.log("response data formatted break: ",formattedBreaks)
      setAllBreaks(formattedBreaks);
    } catch (error: any) {
      // console.log("erreur lors de la récupération des pauses : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // fonction pour formater les cours
  const formattedCourses = () => {
    if (
      !selectedTimetableData?.VersionEDTs ||
      selectedTimetableData.VersionEDTs.length === 0
    ) {
      setLoadingEDT(false);
      return {};
    }

    const version = selectedTimetableData.VersionEDTs[0];
    if (!version.CourseVersionEDTs) {
      setLoadingEDT(false);
      return setCourses(null);
    }

    const coursesData: any = {};

    // Grouper les cours par jour, séance de début et séance de fin
    const groupedBySessionTime: { [key: string]: any[] } = {};
    // console.log("All Courses: ", version.CourseVersionEDTs);
    version.CourseVersionEDTs.forEach((courseVersion: any) => {
      const day = courseVersion.day_course;
      const sessionStartId = courseVersion.SessionStart?.id;
      const sessionEndId = courseVersion.SessionEnd?.id;

      // Créer une clé unique basée sur le jour et les séances
      const key = `${day}_${sessionStartId}_${sessionEndId}`;

      if (!groupedBySessionTime[key]) {
        groupedBySessionTime[key] = [];
      }

      groupedBySessionTime[key].push(courseVersion);
    });

    // Traiter chaque groupe et créer la structure finale
    Object.entries(groupedBySessionTime).forEach(([key, coursesInSlot]) => {
      // Filtrer pour éviter les doublons de cours (même matière, même prof, même salle)
      const uniqueCourses: { [courseKey: string]: any } = {};

      coursesInSlot.forEach((courseVersion: any) => {
        const courseId = courseVersion?.Course?.id;
        const subjectLabel =
          courseVersion?.Course?.Subject?.label || "Matière inconnue";
        const courseKey = `${courseId}_${subjectLabel}`;

        if (!uniqueCourses[courseKey]) {
          uniqueCourses[courseKey] = {
            name: subjectLabel,
            id: courseVersion?.course_version_id || courseId,
            room: courseVersion.Classroom?.label || "Salle inconnue",
            teacherId: courseVersion?.Course?.TeacherProfil?.id,
            teacher: `${courseVersion.Course?.TeacherProfil?.firstname || ""} ${
              courseVersion?.Course?.TeacherProfil?.lastname || ""
            }`.trim(),
            classes: [],
            courseVersionIds: [],
            sessionStart: courseVersion.SessionStart,
            sessionEnd: courseVersion.SessionEnd,
            state_course: courseVersion.state_course,
            type_course: courseVersion.type_course,
            date_course: courseVersion.date_course,
            classroom: courseVersion.Classroom,
          };
        }

        // Ajouter la classe si elle n'existe pas déjà
        const classLabel = courseVersion?.Class
          ? `${courseVersion?.Class.Level?.abbreviation || ""}-${
              courseVersion?.Class.abbreviation || ""
            }`.trim()
          : "Classe inconnue";

        if (
          !uniqueCourses[courseKey].classes.some(
            (c: any) => c.id === courseVersion?.Class?.id,
          )
        ) {
          uniqueCourses[courseKey].classes.push({
            id: courseVersion?.Class?.id,
            label: classLabel,
            fullLabel: courseVersion?.Class?.label || "Classe inconnue",
          });
        }
        // Ajouter l'ID de la version de cours si elle n'existe pas déjà
        if (
          !uniqueCourses[courseKey].courseVersionIds.some(
            (c: any) => c === courseVersion?.course_version_id,
          )
        ) {
          uniqueCourses[courseKey].courseVersionIds.push(
            courseVersion?.course_version_id || "",
          );
        }
      });

      // Remplir la structure des cours par jour et séance
      const firstCourse = coursesInSlot[0];
      const day = firstCourse.day_course;
      const sessionId = firstCourse.SessionStart?.id;

      if (!coursesData[day]) {
        coursesData[day] = {};
      }

      // Ajouter chaque cours unique sous la clé de la séance
      Object.entries(uniqueCourses).forEach(([_, course]: [string, any]) => {
        if (!coursesData[day][sessionId]) {
          coursesData[day][sessionId] = [];
        }
        coursesData[day][sessionId].push(course);
      });
    });
    // console.log("Course Data: ", coursesData);
    setCourses(coursesData);
    setLoadingEDT(false);
  };

  // console.log("EDT is Open ?: ",id_class,"   ",isOpen);
  useEffect(() => {
    if(!isOpen) return; 

    fetchWorkingDays();
    fetchAllSessions();
    fetchEDTBreaksByYear();
  }, [selectedYear,isOpen]);

  useEffect(() => {
    setCourses(null);
    formattedCourses();
  }, [selectedTimetableData, sessions]);

  return (
    <>
      <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
        <EDTTooltipOption
          selectedClass={{ id: id_class }}
          onChange={(t) => setSelectedTimetable(t)}
        />
      </div>

      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
          <h5 className="mb-3">
            {selectedTimetable
              ? `${selectedTimetableData?.edt_title} (${new Date(
                  selectedTimetableData?.valid_from,
                ).toLocaleDateString("fr-FR")} au ${new Date(
                  selectedTimetableData?.valid_until,
                ).toLocaleDateString("fr-FR")})`
              : "Sélectionner un emplois du temps"}{" "}
          </h5>
        </div>
        <div className="card-body pb-0">
          <div style={{ overflowX: "auto" }}>
            {loadingEDT ? (
              <div className="text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Chargement...</span>
                </div>
              </div>
            ) : courses ? (
              <table className="table table-bordered text-center">
                <thead>
                  <tr>
                    {days.map((day) => (
                      <th key={day?.id} className="p-2">
                        {day?.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-gray-500" style={{ overflow: "auto" }}>
                  {sessions.map((session) => {
                    const hasCoursesInSession = days.some(
                      (day) =>
                        (courses[day?.name]?.[session.id] || []).length > 0,
                    );

                    const isSessionCovered = days.some((day) => {
                      const allCoursesInDay = Object.values(
                        courses[day?.name] || {},
                      ).flat();

                      return allCoursesInDay.some((course: any) => {
                        const courseStart = toMinutes(
                          course.sessionStart?.start_time?.slice(0, 5),
                        );
                        const courseEnd = toMinutes(
                          course.sessionEnd?.end_time?.slice(0, 5),
                        );
                        const sessionStart = toMinutes(session.start_time);
                        const sessionEnd = toMinutes(session.end_time);

                        return (
                          courseStart <= sessionStart &&
                          sessionEnd <= courseEnd &&
                          !(
                            courseStart === sessionStart &&
                            courseEnd === sessionEnd
                          )
                        );
                      });
                    });

                    if (!hasCoursesInSession && !isSessionCovered) return null;

                    return (
                      <tr
                        key={session.id}
                        style={{
                          backgroundColor: "gray",
                        }}
                      >
                        {days.map((day) => {
                          const allCoursesInDay = Object.values(
                            courses[day?.name] || {},
                          ).flat();

                          const coveringCourses = allCoursesInDay.filter(
                            (course: any) => {
                              const courseStart = toMinutes(
                                course.sessionStart?.start_time?.slice(0, 5),
                              );
                              const courseEnd = toMinutes(
                                course.sessionEnd?.end_time?.slice(0, 5),
                              );
                              const sessionStart = toMinutes(
                                session.start_time,
                              );
                              const sessionEnd = toMinutes(session.end_time);

                              return (
                                courseStart <= sessionStart &&
                                sessionEnd <= courseEnd
                              );
                            },
                          );
                          // console.log("Covering coures: ", coveringCourses);
                          let rowSpan = 1;
                          if (coveringCourses.length > 0) {
                            const course: any = coveringCourses[0];
                            const courseStart = toMinutes(
                              course.sessionStart?.start_time?.slice(0, 5),
                            );
                            const courseEnd = toMinutes(
                              course.sessionEnd?.end_time?.slice(0, 5),
                            );

                            // Compter combien de séances complètes sont couvertes par ce cours
                            const coveredSessions = sessions.filter(
                              (s: any) => {
                                const sStart = toMinutes(s.start_time);
                                const sEnd = toMinutes(s.end_time);
                                return (
                                  sStart >= courseStart && sEnd <= courseEnd
                                );
                              },
                            );

                            rowSpan = coveredSessions.length;
                          }

                          const sessionStart = toMinutes(session.start_time);
                          const shouldRender =
                            coveringCourses.length === 0 ||
                            coveringCourses.some((course: any) => {
                              const courseStart = toMinutes(
                                course.sessionStart?.start_time?.slice(0, 5),
                              );
                              return courseStart === sessionStart;
                            });

                          if (!shouldRender) return null;

                          return (
                            <td
                              key={day?.id}
                              rowSpan={rowSpan}
                              className="p-0"
                              style={{
                                width: "70px",
                                height: "70px",
                                verticalAlign: "stretch",
                                // border: "1px solid #000",
                                border: "none",
                              }}
                            >
                              <div
                                style={{
                                  height: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "4px",
                                  padding: "4px",
                                  backgroundColor: "#f0f0f0",
                                }}
                              >
                                {coveringCourses.length > 0 ? (
                                  coveringCourses.map(
                                    (course: any, idx: number) => (
                                      <div
                                        className="rounded shadow-sm text-white flex-grow-1"
                                        style={{
                                          backgroundColor: getColorPair(course.teacherId ?? course.teacher ?? course.name).light,
                                          border: `1px solid ${
                                            getColorPair(course.teacherId ?? course.teacher ?? course.name).dark
                                          }`,
                                          display: "flex",
                                          flexDirection: "column",
                                          justifyContent: "space-between",
                                          alignContent: "center",
                                          padding: "6px 8px",
                                          fontSize: "12px",
                                          cursor: "pointer",
                                          minWidth: "200px",
                                        }}
                                      >
                                        <div className="d-flex flex-column h-100 justify-content-between align-items-center gap-2 mb-1">
                                          {/* horaire du cours */}
                                          <p
                                            className="small text-muted mb-1 text-dark"
                                            style={{ fontSize: "12px" }}
                                          >
                                            <i className="ti ti-clock me-1" />
                                            {course.sessionStart?.start_time?.slice(
                                              0,
                                              5,
                                            )}{" "}
                                            -{" "}
                                            {course.sessionEnd?.end_time?.slice(
                                              0,
                                              5,
                                            )}
                                          </p>

                                          <OverlayTrigger
                                            placement="top"
                                            overlay={
                                              <Tooltip id={`tooltip-${idx}`}>
                                                {course.name}
                                              </Tooltip>
                                            }
                                          >
                                            <p
                                              className="fw-semibold mb-1 w-100"
                                              style={{
                                                color: getColorPair(course.teacherId ?? course.teacher ?? course.name)
                                                  .dark,
                                                fontSize: "15px",
                                                textAlign: "center",
                                                textWrap: "wrap",
                                                // overflow: "hidden",
                                                lineHeight: "1.2em",
                                              }}
                                            >
                                              {course.name}
                                            </p>
                                          </OverlayTrigger>
                                          <p
                                            className="text-muted mb-2 small text-truncate text-dark"
                                            style={{
                                              fontSize: "12px",
                                            }}
                                          >
                                            {course.room}
                                          </p>
                                        </div>
                                        {/* Professeur qui fait le cours */}
                                        <div className="d-flex align-items-center mb-2">
                                          <Avvvatars
                                            value={course.teacher}
                                            style="character"
                                            size={40}
                                          />
                                          <div className="ms-2 d-flex flex-column">
                                            <span
                                              className="fw-bold text-truncate text-dark"
                                              title={course.teacher}
                                              style={{
                                                fontSize: "0.95rem",
                                              }}
                                            >
                                              {course.teacher}
                                            </span>
                                          </div>
                                        </div>
                                        {course.classes.length > 0 && (
                                          <p
                                            className="small mb-0 text-dark w-100"
                                            style={{
                                              fontSize: "11px",
                                              textWrap: "wrap",
                                              lineHeight: "1.2em",
                                              textAlign: "center",
                                            }}
                                          >
                                            <i className="ti ti-users me-1" />
                                            {course.classes
                                              .map((c: any) => c.label)
                                              .join(", ")}
                                          </p>
                                        )}
                                      </div>
                                    ),
                                  )
                                ) : (
                                  <div
                                    style={{
                                      flex: 1,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      backgroundColor: "#fff",
                                      borderRadius: "0.25rem",
                                    }}
                                  >
                                    <span className="text-muted small">
                                      Aucun cours
                                    </span>
                                  </div>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="w-100 h-100 text-center">Aucun cours prévus.</div>
            )}
          </div>
        </div>

        <div className="card-footer border-0 pb-0">
          <div className="row">
            {allBreaks.map((brk: any) => (
              <div className="col-lg-4 col-xxl-3 d-flex">
                <div className="card flex-fill">
                  <div className="card-body">
                    <span className="bg-info badge badge-sm mb-2">
                      {brk?.label}
                    </span>
                    <p className="text-dark">
                      <i className="ti ti-clock me-1" />
                      {brk?.start} à {brk?.end}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentEdtPage;
